import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { Model, ModelFormData } from '../types/model';
import FormSlider from './FormSlider';
import api from '../services/api';
import '../styles/DrawerForm.css';
import '../styles/Buttons.css';

interface ModelFormSliderProps {
  workspaceId: string;
  modelId?: string;
  onSuccess: () => void;
  onCancel: () => void;
}

interface Project {
  _id: string;
  name: string;
  views?: string[];
  spaces?: string[];
}

const ModelFormSlider: React.FC<ModelFormSliderProps> = ({
  workspaceId,
  modelId,
  onSuccess,
  onCancel
}) => {
  const [activeTab, setActiveTab] = useState<'model' | 'views' | 'spaces'>('model');
  const [projects, setProjects] = useState<Project[]>([]);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [projectViews, setProjectViews] = useState<string[]>([]);
  const [projectSpaces, setProjectSpaces] = useState<string[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<string>('');
  const [newViewInput, setNewViewInput] = useState('');
  const [addingView, setAddingView] = useState(false);
  const [newSpaceInput, setNewSpaceInput] = useState('');
  const [addingSpace, setAddingSpace] = useState(false);

  const initialFormData: ModelFormData = {
    name: '',
    projectId: '',
    bed: '',
    bath: '',
    den: '',
    flex: '',
    media: '',
    ceiling: '',
    area: '',
    outdoorType: '',
    outdoorArea: '',
    type: '',
    modelCollection: '',
    buildingType: '',
    eligibleToBuy: {
      parking: false,
      locker: false,
      bicycle: false
    },
    notes: '',
    views: [],
    newView: '',
    spaces: [],
    newSpaceName: '',
    newSpaceSize: ''
  };

  useEffect(() => {
    fetchProjects();
  }, [workspaceId]);

  // Update project views when selected project changes
  useEffect(() => {
    if (selectedProjectId) {
      const selectedProject = projects.find(p => p._id === selectedProjectId);
      if (selectedProject) {
        setProjectViews(selectedProject.views || []);
        setProjectSpaces(selectedProject.spaces || []);
      }
    } else {
      setProjectViews([]);
      setProjectSpaces([]);
    }
  }, [selectedProjectId, projects]);

  const fetchProjects = async () => {
    try {
      setLoadingProjects(true);
      const response = await api.get(`/workspaces/${workspaceId}/projects`, {
        params: {
          limit: 1000,
          sortBy: 'name',
          sortOrder: 'asc'
        }
      });
      setProjects(response.data.projects);
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setLoadingProjects(false);
    }
  };

  // Add new view to project and update local state
  const handleAddNewView = async (handleFieldChange: (name: keyof ModelFormData, value: unknown) => void, currentViews: string[]) => {
    const trimmedView = newViewInput.trim();
    if (!trimmedView || !selectedProjectId) return;

    // Check if view already exists
    if (projectViews.includes(trimmedView)) {
      toast.error('This view already exists in the project');
      return;
    }

    try {
      setAddingView(true);
      const updatedViews = [...projectViews, trimmedView];

      // Update project with new view
      await api.put(`/workspaces/${workspaceId}/projects/${selectedProjectId}`, {
        views: updatedViews
      });

      // Update local state
      setProjectViews(updatedViews);

      // Also update the projects array to keep it in sync
      setProjects(prev => prev.map(p =>
        p._id === selectedProjectId ? { ...p, views: updatedViews } : p
      ));

      // Auto-select the new view for this model
      handleFieldChange('views', [...currentViews, trimmedView]);

      // Clear input
      setNewViewInput('');
      toast.success('View added successfully');
    } catch (error: any) {
      console.error('Error adding view:', error);
      toast.error(error.response?.data?.message || 'Failed to add view');
    } finally {
      setAddingView(false);
    }
  };

  // Add new space to project and update local state
  const handleAddNewSpace = async (handleFieldChange: (name: keyof ModelFormData, value: unknown) => void, currentSpaces: Array<{ name: string; size: string }>) => {
    const trimmedSpace = newSpaceInput.trim();
    if (!trimmedSpace || !selectedProjectId) return;

    // Check if space already exists
    if (projectSpaces.includes(trimmedSpace)) {
      toast.error('This space already exists in the project');
      return;
    }

    try {
      setAddingSpace(true);
      const updatedSpaces = [...projectSpaces, trimmedSpace];

      // Update project with new space
      await api.put(`/workspaces/${workspaceId}/projects/${selectedProjectId}`, {
        spaces: updatedSpaces
      });

      // Update local state
      setProjectSpaces(updatedSpaces);

      // Also update the projects array to keep it in sync
      setProjects(prev => prev.map(p =>
        p._id === selectedProjectId ? { ...p, spaces: updatedSpaces } : p
      ));

      // Auto-select the new space for this model (with empty size)
      handleFieldChange('spaces', [...currentSpaces, { name: trimmedSpace, size: '' }]);

      // Clear input
      setNewSpaceInput('');
      toast.success('Space added successfully');
    } catch (error: any) {
      console.error('Error adding space:', error);
      toast.error(error.response?.data?.message || 'Failed to add space');
    } finally {
      setAddingSpace(false);
    }
  };

  const validateForm = (data: ModelFormData): string | null => {
    if (activeTab === 'model') {
      if (!data.name.trim()) {
        return 'Model name is required';
      }
      if (!data.projectId) {
        return 'Project selection is required';
      }
      if (!data.bed || !data.bath) {
        return 'Beds and baths are required';
      }
    }
    return null;
  };

  const transformFetchData = (response: any): ModelFormData => {
    const model: Model = response.model;
    return {
      name: model.name || '',
      projectId: model.projectId || '',
      bed: model.bed?.toString() || '',
      bath: model.bath?.toString() || '',
      den: model.den?.toString() || '',
      flex: model.flex?.toString() || '',
      media: model.media?.toString() || '',
      ceiling: model.ceiling?.toString() || '',
      area: model.area?.toString() || '',
      outdoorType: model.outdoorType || '',
      outdoorArea: model.outdoorArea?.toString() || '',
      type: model.type || '',
      modelCollection: model.modelCollection || '',
      buildingType: model.buildingType || '',
      eligibleToBuy: model.eligibleToBuy || { parking: false, locker: false, bicycle: false },
      notes: model.description || '',
      views: model.views || [],
      newView: '',
      spaces: (model.spaces || []).map(space => typeof space === 'string' ? { name: space, size: '' } : { name: space.name, size: space.size?.toString() || '' }),
      newSpaceName: '',
      newSpaceSize: ''
    };
  };

  const transformSubmitData = (data: ModelFormData) => {
    // Get builderId from selected project
    const selectedProject = projects.find(p => p._id === data.projectId);

    return {
      name: data.name.trim(),
      projectId: data.projectId,
      builderId: (selectedProject as any)?.builderId || '', // Assuming project has builderId
      bed: data.bed ? parseInt(data.bed) : undefined,
      bath: data.bath ? parseInt(data.bath) : undefined,
      den: data.den ? parseInt(data.den) : undefined,
      flex: data.flex ? parseInt(data.flex) : undefined,
      media: data.media ? parseInt(data.media) : undefined,
      ceiling: data.ceiling ? parseFloat(data.ceiling) : undefined,
      area: data.area ? parseFloat(data.area) : undefined,
      outdoorType: data.outdoorType || undefined,
      outdoorArea: data.outdoorArea ? parseFloat(data.outdoorArea) : undefined,
      type: data.type.trim() || undefined,
      modelCollection: data.modelCollection.trim() || undefined,
      buildingType: data.buildingType || undefined,
      eligibleToBuy: data.eligibleToBuy,
      description: data.notes.trim() || undefined,
      views: data.views,
      spaces: data.spaces.map(s => ({
        name: s.name,
        size: s.size ? parseFloat(s.size) : undefined
      }))
    };
  };

  // Don't render the form until projects are loaded for new models
  if (!modelId && loadingProjects) {
    return (
      <div className="builders-loading" style={{ padding: '64px 0' }}>
        <div className="builders-loading-spinner" />
      </div>
    );
  }

  return (
    <div>
      {/* Tabs */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', backgroundColor: '#D4CBC4', padding: '10px', borderRadius: '8px' }}>
        <button
          type="button"
          onClick={() => setActiveTab('model')}
          style={{
            padding: '10px 30px',
            borderRadius: '20px',
            border: 'none',
            backgroundColor: activeTab === 'model' ? '#48182F' : 'transparent',
            color: activeTab === 'model' ? 'white' : '#6B7280',
            cursor: 'pointer',
            fontWeight: '500'
          }}
        >
          Model
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('views')}
          style={{
            padding: '10px 30px',
            borderRadius: '20px',
            border: 'none',
            backgroundColor: activeTab === 'views' ? '#48182F' : 'transparent',
            color: activeTab === 'views' ? 'white' : '#6B7280',
            cursor: 'pointer',
            fontWeight: '500'
          }}
        >
          Views
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('spaces')}
          style={{
            padding: '10px 30px',
            borderRadius: '20px',
            border: 'none',
            backgroundColor: activeTab === 'spaces' ? '#48182F' : 'transparent',
            color: activeTab === 'spaces' ? 'white' : '#6B7280',
            cursor: 'pointer',
            fontWeight: '500'
          }}
        >
          Spaces
        </button>
      </div>

      <FormSlider
        workspaceId={workspaceId}
        itemId={modelId}
        onSuccess={onSuccess}
        onCancel={onCancel}
        apiEndpoint={`/workspaces/${workspaceId}/models`}
        initialFormData={initialFormData}
        validateForm={validateForm}
        transformFetchData={transformFetchData}
        transformSubmitData={transformSubmitData}
        formId="model-form"
      >
        {({ formData, handleInputChange, handleFieldChange, loading }) => {
          // Sync selectedProjectId when formData.projectId changes (e.g., in edit mode)
          if (formData.projectId && formData.projectId !== selectedProjectId) {
            // Use setTimeout to avoid updating state during render
            setTimeout(() => setSelectedProjectId(formData.projectId), 0);
          }
          return (
          <>
            {/* Model Tab */}
            {activeTab === 'model' && (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                  <div className="form-group">
                    <label htmlFor="projectId" className="form-label">
                      Project
                    </label>
                    {loadingProjects ? (
                      <div className="builders-loading" style={{ padding: '20px 0', textAlign: 'center' }}>
                        <div className="builders-loading-spinner" />
                        <p style={{ marginTop: '10px', fontSize: '13px', color: '#6B7280' }}>Loading projects...</p>
                      </div>
                    ) : (
                      <>
                        <select
                          id="projectId"
                          name="projectId"
                          className="form-input"
                          value={formData.projectId}
                          onChange={(e) => {
                            handleInputChange(e);
                            setSelectedProjectId(e.target.value);
                          }}
                          required
                          disabled={loading || !!modelId}
                        >
                          <option value="">Select a project</option>
                          {projects.map((project) => (
                            <option key={project._id} value={project._id}>
                              {project.name}
                            </option>
                          ))}
                        </select>
                        {modelId && (
                          <small style={{ display: 'block', marginTop: '5px', fontSize: '12px', color: '#6B7280' }}>
                            Project cannot be changed after creation
                          </small>
                        )}
                      </>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="name" className="form-label">
                      Model Name
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      className="form-input"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      disabled={loading}
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '15px' }}>
                  <div className="form-group">
                    <label htmlFor="bed" className="form-label">
                      Beds
                    </label>
                    <input
                      type="number"
                      id="bed"
                      name="bed"
                      className="form-input"
                      value={formData.bed}
                      onChange={handleInputChange}
                      required
                      disabled={loading}
                      min="0"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="bath" className="form-label">
                      Bath
                    </label>
                    <input
                      type="number"
                      id="bath"
                      name="bath"
                      className="form-input"
                      value={formData.bath}
                      onChange={handleInputChange}
                      required
                      disabled={loading}
                      min="0"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="den" className="form-label">
                      Den:
                    </label>
                    <input
                      type="number"
                      id="den"
                      name="den"
                      className="form-input"
                      value={formData.den}
                      onChange={handleInputChange}
                      disabled={loading}
                      min="0"
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '15px' }}>
                  <div className="form-group">
                    <label htmlFor="flex" className="form-label">
                      Flex:
                    </label>
                    <input
                      type="number"
                      id="flex"
                      name="flex"
                      className="form-input"
                      value={formData.flex}
                      onChange={handleInputChange}
                      disabled={loading}
                      min="0"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="media" className="form-label">
                      Media:
                    </label>
                    <input
                      type="number"
                      id="media"
                      name="media"
                      className="form-input"
                      value={formData.media}
                      onChange={handleInputChange}
                      disabled={loading}
                      min="0"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="ceiling" className="form-label">
                      Ceiling (Ft.):
                    </label>
                    <input
                      type="number"
                      id="ceiling"
                      name="ceiling"
                      className="form-input"
                      value={formData.ceiling}
                      onChange={handleInputChange}
                      disabled={loading}
                      step="0.1"
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '15px' }}>
                  <div className="form-group">
                    <label htmlFor="area" className="form-label">
                      Area (Sq.Ft):
                    </label>
                    <input
                      type="number"
                      id="area"
                      name="area"
                      className="form-input"
                      value={formData.area}
                      onChange={handleInputChange}
                      disabled={loading}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="outdoorType" className="form-label">
                      Outdoor Type:
                    </label>
                    <select
                      id="outdoorType"
                      name="outdoorType"
                      className="form-input"
                      value={formData.outdoorType}
                      onChange={handleInputChange}
                      disabled={loading}
                    >
                      <option value=""></option>
                      <option value="Balcony">Balcony</option>
                      <option value="Terrace">Terrace</option>
                      <option value="Patio">Patio</option>
                      <option value="None">None</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="outdoorArea" className="form-label">
                      Outdoor Area (Sq.Ft):
                    </label>
                    <input
                      type="number"
                      id="outdoorArea"
                      name="outdoorArea"
                      className="form-input"
                      value={formData.outdoorArea}
                      onChange={handleInputChange}
                      disabled={loading}
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '15px' }}>
                  <div className="form-group">
                    <label htmlFor="type" className="form-label">
                      Type:
                    </label>
                    <input
                      type="text"
                      id="type"
                      name="type"
                      className="form-input"
                      value={formData.type}
                      onChange={handleInputChange}
                      disabled={loading}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="modelCollection" className="form-label">
                      Collection:
                    </label>
                    <input
                      type="text"
                      id="modelCollection"
                      name="modelCollection"
                      className="form-input"
                      value={formData.modelCollection}
                      onChange={handleInputChange}
                      disabled={loading}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="buildingType" className="form-label">
                      Building Type:
                    </label>
                    <select
                      id="buildingType"
                      name="buildingType"
                      className="form-input"
                      value={formData.buildingType}
                      onChange={handleInputChange}
                      disabled={loading}
                    >
                      <option value="">Select building type</option>
                      <option value="Condominium">Condominium</option>
                      <option value="Townhouse">Townhouse</option>
                      <option value="Detached">Detached</option>
                      <option value="Semi-Detached">Semi-Detached</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Eligible to Buy:</label>
                  <div style={{ display: 'flex', flexDirection: 'row', gap: '24px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={formData.eligibleToBuy.parking}
                        onChange={(e) => handleFieldChange('eligibleToBuy', {
                          ...formData.eligibleToBuy,
                          parking: e.target.checked
                        })}
                        disabled={loading}
                        style={{
                          accentColor: '#48182F',
                          width: '16px',
                          height: '16px',
                          cursor: 'pointer'
                        }}
                      />
                      Parking
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={formData.eligibleToBuy.locker}
                        onChange={(e) => handleFieldChange('eligibleToBuy', {
                          ...formData.eligibleToBuy,
                          locker: e.target.checked
                        })}
                        disabled={loading}
                        style={{
                          accentColor: '#48182F',
                          width: '16px',
                          height: '16px',
                          cursor: 'pointer'
                        }}
                      />
                      Locker
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={formData.eligibleToBuy.bicycle}
                        onChange={(e) => handleFieldChange('eligibleToBuy', {
                          ...formData.eligibleToBuy,
                          bicycle: e.target.checked
                        })}
                        disabled={loading}
                        style={{
                          accentColor: '#48182F',
                          width: '16px',
                          height: '16px',
                          cursor: 'pointer'
                        }}
                      />
                      Bicycle
                    </label>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="notes" className="form-label">
                    Notes :
                  </label>
                  <textarea
                    id="notes"
                    name="notes"
                    className="form-input"
                    value={formData.notes}
                    onChange={handleInputChange}
                    disabled={loading}
                    rows={4}
                    style={{ resize: 'vertical' }}
                  />
                </div>
              </>
            )}

            {/* Views Tab */}
            {activeTab === 'views' && (
              <>
                <p style={{ color: '#6B7280', marginBottom: '20px' }}>
                  Select views apply to this model :
                </p>
                {!formData.projectId ? (
                  <p style={{ color: '#9CA3AF', fontStyle: 'italic' }}>
                    Please select a project first to see available views.
                  </p>
                ) : (
                  <>
                    {projectViews.length === 0 ? (
                      <p style={{ color: '#9CA3AF', fontStyle: 'italic', marginBottom: '20px' }}>
                        No views configured for this project. Add views below.
                      </p>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '20px' }}>
                        {projectViews.map((view) => (
                          <label key={view} style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                            <input
                              type="checkbox"
                              checked={formData.views.includes(view)}
                              onChange={(e) => {
                                const newViews = e.target.checked
                                  ? [...formData.views, view]
                                  : formData.views.filter(v => v !== view);
                                handleFieldChange('views', newViews);
                              }}
                              disabled={loading}
                            />
                            {view}
                          </label>
                        ))}
                      </div>
                    )}

                    {/* Add new view input */}
                    <div className="form-group">
                      <input
                        type="text"
                        className="form-input"
                        placeholder="Views"
                        value={newViewInput}
                        onChange={(e) => setNewViewInput(e.target.value)}
                        disabled={loading || addingView}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter' && newViewInput.trim()) {
                            e.preventDefault();
                            handleAddNewView(handleFieldChange, formData.views);
                          }
                        }}
                      />
                      <button
                        type="button"
                        className="btn btn-primary btn-md"
                        style={{ marginTop: '10px' }}
                        onClick={() => handleAddNewView(handleFieldChange, formData.views)}
                        disabled={loading || addingView || !newViewInput.trim()}
                      >
                        {addingView ? 'Adding...' : 'Add'}
                      </button>
                    </div>
                  </>
                )}
              </>
            )}

            {/* Spaces Tab */}
            {activeTab === 'spaces' && (
              <>
                <p style={{ color: '#6B7280', marginBottom: '20px' }}>
                  Select Spaces apply to this model :
                </p>
                {!formData.projectId ? (
                  <p style={{ color: '#9CA3AF', fontStyle: 'italic' }}>
                    Please select a project first to see available spaces.
                  </p>
                ) : (
                  <>
                    {projectSpaces.length === 0 ? (
                      <p style={{ color: '#9CA3AF', fontStyle: 'italic', marginBottom: '20px' }}>
                        No spaces configured for this project. Add spaces below.
                      </p>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '20px' }}>
                        {projectSpaces.map((space) => {
                          const isSelected = formData.spaces.some(s => s.name === space);
                          const selectedSpace = formData.spaces.find(s => s.name === space);
                          return (
                            <div key={space} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                              <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', flex: 1 }}>
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      handleFieldChange('spaces', [...formData.spaces, { name: space, size: '' }]);
                                    } else {
                                      handleFieldChange('spaces', formData.spaces.filter(s => s.name !== space));
                                    }
                                  }}
                                  disabled={loading}
                                />
                                {space}
                              </label>
                              {isSelected && (
                                <input
                                  type="text"
                                  className="form-input"
                                  placeholder="Size"
                                  value={selectedSpace?.size || ''}
                                  onChange={(e) => {
                                    handleFieldChange('spaces', formData.spaces.map(s =>
                                      s.name === space ? { ...s, size: e.target.value } : s
                                    ));
                                  }}
                                  disabled={loading}
                                  style={{ width: '100px' }}
                                />
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Add new space input */}
                    <div className="form-group">
                      <input
                        type="text"
                        className="form-input"
                        placeholder="Spaces"
                        value={newSpaceInput}
                        onChange={(e) => setNewSpaceInput(e.target.value)}
                        disabled={loading || addingSpace}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter' && newSpaceInput.trim()) {
                            e.preventDefault();
                            handleAddNewSpace(handleFieldChange, formData.spaces);
                          }
                        }}
                      />
                      <button
                        type="button"
                        className="btn btn-primary btn-md"
                        style={{ marginTop: '10px' }}
                        onClick={() => handleAddNewSpace(handleFieldChange, formData.spaces)}
                        disabled={loading || addingSpace || !newSpaceInput.trim()}
                      >
                        {addingSpace ? 'Adding...' : 'Add'}
                      </button>
                    </div>
                  </>
                )}
              </>
            )}
          </>
          );
        }}
      </FormSlider>
    </div>
  );
};

export default ModelFormSlider;
